import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Flex } from '@instructure/ui-flex';
import { SimpleSelect } from '@instructure/ui-simple-select';

const GradeRangeSelector = ({
  id,
  name,
  grades = [],
  values = [],
  onChange,
  rangeStartLabel = 'Start',
  rangeEndLabel = 'End'
}) => {
  const getIndexForValue = useCallback((value) => {
    return grades.findIndex(grade => grade.value === value);
  }, [grades]);

  const sortedValuesIndex = useMemo(() => {
    return values
      .map(value => getIndexForValue(value))
      .sort((a, b) => a - b);
  }, [values, getIndexForValue]);

  const firstValueIndex = useMemo(() => {
    return sortedValuesIndex[0];
  }, [sortedValuesIndex]);

  const lastValueIndex = useMemo(() => {
    return sortedValuesIndex[sortedValuesIndex.length - 1];
  }, [sortedValuesIndex]);

  const handleChange = useCallback((startIndex, endIndex) => {
    const newValues = grades.slice(startIndex, endIndex + 1).map(grade => grade.value);

    onChange({
      target: {
        name,
        value: newValues
      }
    });
  }, [grades, name, onChange]);

  const handleStartChange = useCallback((_, { value }) => {
    const startIndex = getIndexForValue(value);
    handleChange(startIndex, lastValueIndex);
  }, [getIndexForValue, handleChange, lastValueIndex]);

  const handleEndChange = useCallback((_, { value }) => {
    const endIndex = getIndexForValue(value);
    handleChange(firstValueIndex, endIndex);
  }, [getIndexForValue, handleChange, firstValueIndex]);

  const isValuesEmpty = values.length === 0;

  return (
    <Flex as="div" justifyItems="space-between" gap="medium" wrap="wrap">
      <Flex.Item shouldGrow>
        <SimpleSelect
          id={id + '-start'}
          renderLabel={rangeStartLabel}
          value={isValuesEmpty ? grades[0].value : grades[firstValueIndex].value}
          onChange={handleStartChange}
        >
          {grades.map(grade => (
            <SimpleSelect.Option
              id={grade.value}
              key={grade.value}
              value={grade.value}
              isDisabled={isValuesEmpty ? false : getIndexForValue(grade.value) > lastValueIndex}>
              {grade.label}
            </SimpleSelect.Option>
          ))}
        </SimpleSelect>
      </Flex.Item>
      <Flex.Item shouldGrow>
        <SimpleSelect
          id={id + '-end'}
          renderLabel={rangeEndLabel}
          value={isValuesEmpty ? grades[grades.length - 1].value : grades[lastValueIndex].value}
          onChange={handleEndChange}
        >
          {grades.map(grade => (
            <SimpleSelect.Option
              id={grade.value}
              key={grade.value}
              value={grade.value}
              isDisabled={isValuesEmpty ? false : getIndexForValue(grade.value) < firstValueIndex}>
              {grade.label}
            </SimpleSelect.Option>
          ))}
        </SimpleSelect>
      </Flex.Item>
    </Flex>
  );
};

GradeRangeSelector.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  grades: PropTypes.array,
  values: PropTypes.arrayOf(PropTypes.any),
  onChange: PropTypes.func,
  rangeStartLabel: PropTypes.string,
  rangeEndLabel: PropTypes.string
};

export default GradeRangeSelector;
